import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import BackButton from '../components/BackButton';
import { useApiContext } from '../contexts/ApiContext';
import { useHealthCenterContext } from '../contexts/HealthCenterContext';
import toast from '../toast';

export const Route = createFileRoute('/qr-code-scan')({
  component: QRCodeScan,
  beforeLoad: ({ context }) => {
    if (!context.userAppContext.app) {
      throw redirect({ to: '/app-select' });
    }
    if (context.healthCenterContext.healthCenterConfig.id) {
      throw redirect({
        to: '/patient-transport',
      });
    }
  },
});

function QRCodeScan() {
  const { storeApiConfig } = useApiContext();
  const { storeHealthCenterConfig } = useHealthCenterContext();
  const { t } = useTranslation('patient-transport');

  const navigate = Route.useNavigate();

  const { invalidate } = useRouter();
  const handleBack = () => {
    navigate({ to: '/health-center-link' });
  };

  const handleScan = async (data: IDetectedBarcode[]) => {
    if (!data || data.length === 0 || !isValid(data[0].rawValue)) {
      toast.error(t('qrCodeScan.invalidError'));
      return;
    }
    const qrCodeData = JSON.parse(data[0].rawValue);
    storeApiConfig({
      id: qrCodeData.api.key,
      url: rewriteLocalhostUrl(qrCodeData.api.url),
      liveQueryUrl: qrCodeData.api.liveQueryUrl && rewriteLocalhostUrl(qrCodeData.api.liveQueryUrl),
    });
    storeHealthCenterConfig({
      id: qrCodeData.healthCenter.id,
      key: qrCodeData.healthCenter.key,
    });
    await invalidate();
    navigate({ from: '/qr-code-scan', to: '/porter-login' });
  };

  return (
    <Container>
      <BackButton onClick={handleBack} />
      <Scanner
        onScan={handleScan}
        constraints={{ facingMode: 'environment' }}
        components={{ finder: undefined }}
        styles={{
          container: {
            display: 'flex',
            alignItems: 'center',
          },
          video: { objectFit: 'cover', height: '80%', width: '100%' },
        }}
      />
      <Overlay>
        <ScanHelper />
      </Overlay>
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  background-color: black;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScanHelper = styled.div`
  width: 70%;
  height: 50%;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  box-shadow: 0 0 0 100vw rgba(0, 0, 0, 0.5);
`;

/**
 * In dev, QR codes contain localhost URLs (e.g. http://localhost:1337/parse)
 * which are unreachable from a phone. Rewrite them to use the current origin
 * so they go through the Vite proxy.
 */
const rewriteLocalhostUrl = (url: string): string => {
  if (!import.meta.env.DEV) return url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      return `${window.location.origin}${parsed.pathname}`;
    }
  } catch {
    // not a valid URL, return as-is
  }
  return url;
};

const isValid = (data: string) => {
  try {
    const jsonData = JSON.parse(data);
    return jsonData.healthCenter.id && jsonData.healthCenter.key;
  } catch (error) {
    console.error(error);
    return false;
  }
};
